import React, {useEffect, useState} from "react";
import ModalDefault from "../../../../common/components/modal/modal-default";
import {Field, FieldsManager} from "../../../../data/services/fields";
import {CHARGE_TO_COMPANY, chargeToFieldState, chargeToFromItem, chargeToOptions, isChargedToSomeone} from "../../dialogs/charge-to";
import {applyChargeTo} from "../../dialogs/charge-to-apply";
import {fieldsToHtml, fillFieldsFromData} from "../../../../common/util/util-fields";
import Resources from "../../../../data/services/resources";
import {DEFAULT_METADATA_SELECT_SEARCH_QUERY, UPDATE_PERM} from "../../../../util/util-constants";
import {useDispatch, useSelector} from "react-redux";
import {getJWT, getUser} from "../../../../common/util/util-auth";
import ResourceTable from "../../../../common/components/resource-table";
import {
    checkPerm,
    getDefaultContactGroup,
    getDefaultUserOfficeValues,
    getProp,
    openInNewTab
} from "../../../../common/util/util-helpers";
import {getDialogResource} from "../../../../data/actions/dialogResource";
import {getSecondResource} from "../../../../data/actions/secondResource";
import FileViewer from "../../../../common/components/file-viewer/components";
import Env from "../../../../util/env";
import XCircleIcon from "@heroicons/react/24/outline/XCircleIcon";
import PencilAltOutlineIcon from "@heroicons/react/24/outline/PencilSquareIcon";
import {
    ArrowPathIcon,
    ArrowsPointingInIcon,
    EyeIcon,
    PlusIcon,
    ScissorsIcon,
    Squares2X2Icon
} from "@heroicons/react/24/outline";
import FieldCellTextNew from "../../../../common/components/fields/field-cell-text-new";
import FieldCellSelectSearch from "../../../../common/components/fields/field-cell-select-search";
import FieldCellMoney from "../../../../common/components/fields/field-cell-money";
import XMarkIcon from "@heroicons/react/20/solid/XMarkIcon";
import LocalStorage from "../../../../util/localStorage";
import {createResource} from "../../../../data/actions/resource";
import {genericMoneyFormatter, scrollErrorIntoView} from "../../../../common/util/util-vanilla";
import InfoParagraph from "../../../../common/components/info-paragraph";
import {Loader, LoaderLarge} from "../../../../common/components/loader";
import Tippy from "@tippyjs/react";
import Tooltip from "../../../../common/components/tooltip";
import DescriptionCellTooltip from "../../accounting-components/description-cell-tooltip";
import ModalSaveResource from "../../../../common/components/modal/modal-save-resource";
import {
    buildMergedItem,
    buildSplitItems,
    chargeToSelection,
    moneyFieldValue,
    splitRow,
    sumCents
} from "./wo-pay-item-utils";
import {centsToMoney, computeSplitAmounts} from "../../dialogs/invoice-expense-item-table/split-utils";


// Quieter and shorter than the dialog's primary buttons: these reshape existing
// lines rather than committing anything.
const LINE_ACTION_BTN_CLASS = "btn btn-outline-secondary h-7 px-2 gap-1 text-xs font-medium";

const ITEMS_TABLE_OPTIONS = {
    style: {
        condensed: true,
        floatingActions: false,
        frozenActionColumn: false
    }
};

export default function WOPayProcessBill({show, translate, selectedItem, onClose, query}) {

    const dispatch = useDispatch();
    const [fields, setFields] = useState(getFields());
    const [rows, setRows] = useState({});
    const [rowsMisc, setRowsMisc] = useState({});
    const dialogResource = useSelector(state => state.dialogResource)
    const resource = useSelector(state => state.resource)
    const isDialogLoading = getProp(dialogResource, 'isLoading', false)
    const dataItems = getProp(dialogResource, 'data.Items', [])
    const defaults = getProp(resource, 'data.defaults', [])
    const dataMisc = getProp(dialogResource, 'data.Misc', [])
    const secondResource = useSelector(state => state.secondResource)
    const dataDocuments = getProp(secondResource, 'data.list', [])
    const [previewFile, setPreviewFile] = useState(null);
    const [previewOpen, setPreviewOpen] = useState(false);
    const [attachments, setAttachments] = useState([]);
    const [isDocumentPreviewModalOpen, setIsDocumentPreviewModalOpen] = useState(false);
    const [attachmentFile, setAttachmentFile] = useState(null);
    const [internalNotes, setInternalNotes] = useState(selectedItem?.Notes);
    const [isSplitModalOpen, setIsSplitModalOpen] = useState(false);
    const [splitModalKey, setSplitModalKey] = useState(0);
    // null => the toolbar "merge and split"; otherwise the single row to split.
    const [splitTarget, setSplitTarget] = useState(null);
    // {item, rowsVar} — which row the charge-to dialog is editing, and which table it is in.
    const [chargeToTarget, setChargeToTarget] = useState(null);
    const [chargeToModalKey, setChargeToModalKey] = useState(0);

    const renderCells = {
        ActionItem: (item) => {
            return <DescriptionCellTooltip value={item?.['ActionItem']}>
                <FieldCellTextNew
                    name="ActionItem"
                    value={item?.['ActionItem']}
                    onChange={(name, value) => handleItemInputChange(name, value, item)}
                    errorMessage={rows?.[item.index]?.['Description']?.errorMessage}
                />
            </DescriptionCellTooltip>
        },
        AccountID: (item) => {
            return <FieldCellSelectSearch
                name="AccountID"
                value={{value: item?.['AccountID'], label: item?.['Account']}}
                onChange={(name, value) => handleItemInputChange(name, value, item)}
                placeholder={translate("text.no_account_selected")}
                isClearable={!!item?.['AccountID']}
                select={selects["AccountID"]}
                errorMessage={rows?.[item.index]?.['AccountID']?.errorMessage}
            />
        },
        ChargeToContactID: (item) => <button
            onClick={() => handleOpenChargeToModal(item, 'rows')}
            className="hover:text-primary flex items-center gap-2 p-1 rounded-btn"
        >
            {item.ChargeToContact || item.ChargeToOrganization || item.ChargeToTruck || translate('label.ChargeToCompany')}

            <PencilAltOutlineIcon className="w-5 h-5 text-tm-gray-400 shrink-0"/>
        </button>,
        TotalAmount: (item) =>
            <div className="p-1 relative pr-6 h-11 w-32 ml-auto">
                <FieldCellMoney
                    name="TotalAmount"
                    value={item?.['TotalAmount']}
                    onChange={(name, value) => handleItemInputChange(name, value, item)}
                    errorMessage={rows?.[item.index]?.['Amount']?.errorMessage}
                />
            </div>
    }

    const renderMiscCells = {
        Description: (item) => {
            return <DescriptionCellTooltip value={item?.['Description']}>
                <FieldCellTextNew
                    name="Description"
                    value={item?.['Description']}
                    onChange={(name, value) => handleItemMiscInputChange(name, value, item)}
                    errorMessage={rowsMisc?.[item.index]?.['Description']?.errorMessage}
                />
            </DescriptionCellTooltip>
        },
        AccountID: (item) => <FieldCellSelectSearch
            name="AccountID"
            value={{value: item?.['AccountID'], label: item?.['Account']}}
            onChange={(name, value) => handleItemMiscInputChange(name, value, item)}
            placeholder={translate("text.no_account_selected")}
            isClearable={!!item?.['AccountID']}
            select={selects["AccountID"]}
            errorMessage={rowsMisc?.[item.index]?.['AccountID']?.errorMessage}
        />,
        ChargeToContactID: (item) => <button
            onClick={() => handleOpenChargeToModal(item, 'rowsMisc')}
            className="hover:text-primary flex items-center gap-2 p-1 rounded-btn"
        >
            {item.ChargeToContact || item.ChargeToOrganization || item.ChargeToTruck || translate('label.ChargeToCompany')}

            <PencilAltOutlineIcon className="w-5 h-5 text-tm-gray-400 shrink-0"/>
        </button>,
        TotalAmount: (item) =>
            <div className="p-1 relative pr-6 h-11 w-32 ml-auto">
                <FieldCellMoney
                    name="TotalAmount"
                    value={item?.['TotalAmount']}
                    onChange={(name, value) => handleItemMiscInputChange(name, value, item)}
                    errorMessage={rowsMisc?.[item.index]?.['TotalAmount']?.errorMessage}
                />
            </div>
    }

    function getFields() {
        const fieldTemplates = {
            ChargeOrganizationID: new Field('ChargeOrganizationID', !!selectedItem.AssignedVendorOrganizationID ? {
                label: selectedItem?.AssignedVendor,
                value: selectedItem?.AssignedVendorOrganizationID
            } : '', ['empty'], false, 'select-search', {
                addContainerClass: "col-span-4",
                label: "PayTo"
            }),
            CustomerReferenceNumber: new Field("CustomerReferenceNumber", selectedItem?.CustomerReferenceNumber, ['empty'], false, 'text', {addContainerClass: "col-span-2", label: "vendor_reference_number"}),
            Date: new Field('Date', selectedItem?.RequestDate, ['empty'], false, "date", {addContainerClass: "col-span-2"}),
            DueDate: new Field('DueDate', selectedItem?.RequestDate, ['empty'], false, 'date', {addContainerClass: "col-span-2"}),
            BookDate: new Field('BookDate', selectedItem?.RequestDate, ['empty'], false, 'date', {addContainerClass: "col-span-2"}),
            OfficeID: new Field('OfficeID', selectedItem?.OfficeID ? {
                label: selectedItem.Office,
                value: selectedItem.OfficeID
            } : getDefaultUserOfficeValues(), ['empty'], false, 'select-search', {addContainerClass: "col-span-2"}),
            ContactGroupID: new Field('ContactGroupID', selectedItem?.ContactGroupID ? {
                label: selectedItem.ContactGroup,
                value: selectedItem.ContactGroupID
            } : getDefaultContactGroup(), ['empty'], false, 'select-search', {addContainerClass: "col-span-2"}),
            TotalAmount: new Field('TotalAmount', 0, ['empty'], true, 'money', {addContainerClass: "col-span-2"})
        }
        return fieldTemplates;
    }

    // The backend resolves each work order's defaults (truck WO -> WorkOrderTruck*,
    // trailer WO -> WorkOrderTrailer*, else the general account), each independently.
    // The old client-side chain let a truck work order with no truck account fall
    // through to the trailer one. The general accounts from `defaults` only cover an
    // older backend that does not send the resolved fields yet.
    function getAdvancesAccount() {
        if (selectedItem?.DefaultAdvancesAccountID !== undefined) {
            return {value: selectedItem.DefaultAdvancesAccountID, label: selectedItem.DefaultAdvancesAccount};
        }

        return {value: defaults?.AdvancesAccountID, label: defaults?.AdvancesAccount};
    }

    function getExpenseAccount() {
        if (selectedItem?.DefaultExpenseAccountID !== undefined) {
            return {value: selectedItem.DefaultExpenseAccountID, label: selectedItem.DefaultExpenseAccount};
        }

        return {value: defaults?.ExpenseAccountID, label: defaults?.ExpenseAccount};
    }

    function getDefaultAccount(item) {
        // Keyed on the charge-to choice, not on whether the picker happens to be filled
        // in: the user states the intent and the account follows. Same outcome as before
        // for every line that already names a contact.
        return isChargedToSomeone(chargeToFromItem(item)) ? getAdvancesAccount() : getExpenseAccount();
    }

    function getFieldsItems(item = null) {
        const fieldTemplates = {
            ActionItem: new Field('ActionItem', '', [''], false, 'text', {addContainerClass: "col-span-2"}),
            AccountID: new Field('AccountID', '', ['empty'], false, 'select-search', {addContainerClass: "col-span-2"}),
            // Account: new Field('Account', '', [''], false, 'text', {addContainerClass: "col-span-2", hideTable: true}),
            ChargeTo: new Field('ChargeTo', CHARGE_TO_COMPANY, [''], false, 'button-group', {
                data: chargeToOptions(translate, {allowTruck: true}),
                addContainerClass: "col-span-full", hideTable: true
            }),
            // Still the ChargeToContactID column, but the cell shows whichever target the
            // line carries now, so the header is the generic one.
            ChargeToContactID: new Field('ChargeToContactID', '', [''], false, 'select-search', {addContainerClass: "col-span-full", label: "ChargeTo"}),
            ChargeToOrganizationID: new Field('ChargeToOrganizationID', '', [''], false, 'hidden', {addContainerClass: "col-span-full", hideTable: true}),
            ChargeToTruckID: new Field('ChargeToTruckID', '', [''], false, 'hidden', {addContainerClass: "col-span-full", hideTable: true}),
            Qty: new Field("Qty", 1, [''], false, 'float', {addContainerClass: "col-span-2", hideTable: true}),
            Amount: new Field('Amount', '', [''], false, 'money', {addContainerClass: "col-span-2", hideTable: true}),
            TotalAmount: new Field('TotalAmount', '', ['empty'], false, 'money', {addContainerClass: "col-span-2"})
        }

        let filledFields = fillFieldsFromData(fieldTemplates, item)

        const chargeTo = chargeToFromItem(item);
        const chargeToState = chargeToFieldState(chargeTo);
        filledFields.ChargeTo.value = chargeTo;
        Object.entries(chargeToState).forEach(([name, state]) => {
            filledFields[name].type = state.type;
        });
        filledFields.AccountID.value = getDefaultAccount(item);

        return filledFields
    }

    function getMiscFields(item = null) {
        const fieldTemplates = {
            Description: new Field('Description', '', [''], false, 'text', {addContainerClass: "col-span-2"}),
            AccountID: new Field('AccountID', '', ['empty'], false, 'select-search', {addContainerClass: "col-span-2"}),
            // Account: new Field('Account', '', [''], false, 'text', {addContainerClass: "col-span-2", hideTable: true}),
            ChargeTo: new Field('ChargeTo', CHARGE_TO_COMPANY, [''], false, 'button-group', {
                data: chargeToOptions(translate, {allowTruck: true}),
                addContainerClass: "col-span-full", hideTable: true
            }),
            // Still the ChargeToContactID column, but the cell shows whichever target the
            // line carries now, so the header is the generic one.
            ChargeToContactID: new Field('ChargeToContactID', '', [''], false, 'select-search', {addContainerClass: "col-span-full", label: "ChargeTo"}),
            ChargeToOrganizationID: new Field('ChargeToOrganizationID', '', [''], false, 'hidden', {addContainerClass: "col-span-full", hideTable: true}),
            ChargeToTruckID: new Field('ChargeToTruckID', '', [''], false, 'hidden', {addContainerClass: "col-span-full", hideTable: true}),
            Qty: new Field("Qty", 1, [''], false, 'float', {addContainerClass: "col-span-2", hideTable: true}),
            Amount: new Field('Amount', '', [''], false, 'money', {addContainerClass: "col-span-2", hideTable: true}),
            TotalAmount: new Field('TotalAmount', '', ['empty'], false, 'money', {addContainerClass: "col-span-2"})
        }

        let filledFields = fillFieldsFromData(fieldTemplates, item)

        const chargeTo = chargeToFromItem(item);
        const chargeToState = chargeToFieldState(chargeTo);
        filledFields.ChargeTo.value = chargeTo;
        Object.entries(chargeToState).forEach(([name, state]) => {
            filledFields[name].type = state.type;
        });
        filledFields.AccountID.value = getDefaultAccount(item);

        return filledFields
    }

    function getSplitFields() {
        return {
            SplitCount: new Field('SplitCount', 10, ['required'], false, 'integer', {
                addContainerClass: 'col-span-full',
                labelRaw: translate("text.number_of_parts")
            }, {min: 1})
        }
    }

    function getDocumentFields() {
        return {
            Description: new Field("Description", '', [], false, 'textarea'),
            DocumentTypeID: new Field("DocumentTypeID", '', ['empty'], false, 'select'),
            UpdatedByContact: new Field("UpdatedByContact", '', [''], false, 'custom', {
                render: (item) => <>{item.UpdatedByContact ?? this.props.translate("text.system")}</>,
                omitSort: true
            }),
            CreateUpdateDate: new Field("CreateUpdateDate", '', ['empty'], false, 'date')
        }
    }

    const handleFieldsInputChange = (name, value) => {
        setFields(FieldsManager.updateField(fields, name, value))
    }

    function calculateGrandTotal(rows, rowsMisc) {
        const rowsTotal = rows.reduce(
            (sum, row) => sum + (Number(row.TotalAmount?.value) || 0),
            0
        );

        const miscTotal = rowsMisc.reduce(
            (sum, row) => sum + (Number(row.TotalAmount?.value) || 0),
            0
        );

        return (Math.round((rowsTotal + miscTotal) * 100) / 100).toFixed(2);
    }


    function handleItemInputChange(name, value, item) {
        const itemsUpdate = [...rows];

        // A money field holding numeric 0 fails the 'empty' validator, so an
        // amount could never be zeroed out by hand. moneyFieldValue keeps it a
        // truthy "0"; the API still receives a numeric 0.
        itemsUpdate[item.index][name].value = name === "TotalAmount" ? moneyFieldValue(value) : value;
        itemsUpdate[item.index][name].errorMessage = [];


        if (name === "TotalAmount") {
            const grandTotal = calculateGrandTotal(
                itemsUpdate,
                rowsMisc
            );

            setFields(prev =>
                FieldsManager.updateField(prev, "TotalAmount", grandTotal)
            );
        }

        setRows(itemsUpdate);
    }

    function handleItemMiscInputChange(name, value, item) {
        const itemsUpdate = [...rowsMisc];

        // A money field holding numeric 0 fails the 'empty' validator, so an
        // amount could never be zeroed out by hand. moneyFieldValue keeps it a
        // truthy "0"; the API still receives a numeric 0.
        itemsUpdate[item.index][name].value = name === "TotalAmount" ? moneyFieldValue(value) : value;
        itemsUpdate[item.index][name].errorMessage = [];

        if (name === "TotalAmount") {
            const grandTotal = calculateGrandTotal(
                rows,
                itemsUpdate
            );

            setFields(prev =>
                FieldsManager.updateField(prev, "TotalAmount", grandTotal)
            );
        }

        setRowsMisc(itemsUpdate);
    }

    function handleOpenChargeToModal(item, rowsVar) {
        setChargeToTarget({item, rowsVar});
        setChargeToModalKey(prev => prev + 1);
    }

    /**
     * The dialog's own field map: the choice plus the three targets, of which
     * chargeToFieldState reveals exactly one.
     */
    function getChargeToFields(item = {}) {
        const chargeTo = chargeToFromItem(item);
        const chargeToState = chargeToFieldState(chargeTo);

        return {
            ChargeTo: new Field('ChargeTo', chargeTo, [''], false, 'button-group', {
                data: chargeToOptions(translate, {allowTruck: true}),
                addContainerClass: "col-span-full",
                hideLabel: true
            }),
            ChargeToContactID: new Field('ChargeToContactID', item?.ChargeToContactID
                ? {value: item.ChargeToContactID, label: item.ChargeToContact}
                : '', chargeToState.ChargeToContactID.validate, false, chargeToState.ChargeToContactID.type,
                {addContainerClass: "col-span-full"}, {isClearable: true}),
            ChargeToOrganizationID: new Field('ChargeToOrganizationID', item?.ChargeToOrganizationID
                ? {value: item.ChargeToOrganizationID, label: item.ChargeToOrganization}
                : '', chargeToState.ChargeToOrganizationID.validate, false, chargeToState.ChargeToOrganizationID.type,
                {addContainerClass: "col-span-full"}, {isClearable: true}),
            ChargeToTruckID: new Field('ChargeToTruckID', item?.ChargeToTruckID
                ? {value: item.ChargeToTruckID, label: item.ChargeToTruck}
                : '', chargeToState.ChargeToTruckID.validate, false, chargeToState.ChargeToTruckID.type,
                {addContainerClass: "col-span-full"}, {isClearable: true})
        };
    }

    function handleChargeToInputChange(fields, name, value) {
        fields = FieldsManager.updateField(fields, name, value);

        if (name === 'ChargeTo') {
            fields = applyChargeTo(fields, value);
        }

        return fields;
    }

    /**
     * Write the dialog's answer back onto the row, and move the line between the expense
     * and advances account to match — the rule this screen has always had, now keyed on
     * the stated choice rather than on whether a picker happened to be filled in.
     */
    function handleChargeToSubmit(params, dialogFields) {
        const {item, rowsVar} = chargeToTarget;
        const isMisc = rowsVar === 'rowsMisc';
        const update = isMisc ? [...rowsMisc] : [...rows];
        const row = applyChargeTo(update[item.index], params.ChargeTo);

        // Off dialogFields, not params: params flattens a select-search to its id and drops
        // the label, which the table needs to render the row. See chargeToSelection.
        const selection = chargeToSelection(params.ChargeTo, dialogFields);
        if (selection) {
            row[selection.name].value = selection.value;
        }

        row.AccountID.value = isChargedToSomeone(params.ChargeTo) ? getAdvancesAccount() : getExpenseAccount();
        update[item.index] = row;

        if (isMisc) {
            setRowsMisc(update);
        } else {
            setRows(update);
        }
        setChargeToTarget(null);
    }

    /** Field rows -> the plain row shape wo-pay-item-utils works with. */
    function toPlainRows(fieldRows, descriptionField) {
        return Object.values(fieldRows ?? {}).map(row => ({
            Description: row[descriptionField]?.value ?? '',
            TotalAmount: row.TotalAmount?.value,
            ChargeToContactID: row.ChargeToContactID?.value?.value ?? '',
            ChargeToContact: row.ChargeToContactID?.value?.label ?? '',
            ChargeToOrganizationID: row.ChargeToOrganizationID?.value?.value ?? '',
            ChargeToOrganization: row.ChargeToOrganizationID?.value?.label ?? '',
            ChargeToTruckID: row.ChargeToTruckID?.value?.value ?? '',
            ChargeToTruck: row.ChargeToTruckID?.value?.label ?? '',
            AccountID: row.AccountID?.value?.value ?? '',
            Account: row.AccountID?.value?.label ?? ''
        }));
    }

    /** A merged plain row -> an Items Field row, keeping the account the merge
     *  chose. getFieldsItems re-derives AccountID from the Charge To, so the
     *  account has to be re-applied after it runs. */
    function toItemFieldRow(mergedRow) {
        const fieldRow = getFieldsItems({...mergedRow, ActionItem: mergedRow.Description});

        fieldRow.AccountID.value = mergedRow.AccountID
            ? {value: mergedRow.AccountID, label: mergedRow.Account}
            : null;

        return fieldRow;
    }

    function toMiscFieldRow(mergedRow) {
        const fieldRow = getMiscFields(mergedRow);

        fieldRow.AccountID.value = mergedRow.AccountID
            ? {value: mergedRow.AccountID, label: mergedRow.Account}
            : null;

        return fieldRow;
    }

    function getPlainRows() {
        return {
            items: toPlainRows(rows, 'ActionItem'),
            misc: toPlainRows(rowsMisc, 'Description')
        };
    }

    /** The account a merged line falls back to when nothing is charged to anyone. */
    function getMergeFallbackAccount() {
        const expenseAccount = getExpenseAccount();

        return {AccountID: expenseAccount?.value, Account: expenseAccount?.label};
    }

    function handleMergeAll() {
        const {items, misc} = getPlainRows();

        if (!items.length && !misc.length) return;

        setRows([toItemFieldRow(buildMergedItem(items, misc, getMergeFallbackAccount()))]);
        setRowsMisc([]);
    }

    function handleMergeAndSplit(parts) {
        const {items, misc} = getPlainRows();

        if (!items.length && !misc.length) return;

        const splitRows = buildSplitItems(items, misc, parts, getMergeFallbackAccount());

        setRows(splitRows.map(toItemFieldRow));
        setRowsMisc([]);
    }

    function handleResetItems() {
        setRows(buildRowsFromData());
        setRowsMisc(Array.isArray(dataMisc) ? buildMiscRowsFromData() : []);
    }

    /** Split a single row in place, leaving every other row untouched. */
    function handleSplitSingleRow(target, parts) {
        const isMisc = target.table === 'misc';
        const sourceRows = isMisc ? rowsMisc : rows;
        const descriptionField = isMisc ? 'Description' : 'ActionItem';
        const plainRow = toPlainRows(sourceRows, descriptionField)[target.index];

        if (!plainRow) return;

        const children = splitRow(plainRow, parts)
            .map(isMisc ? toMiscFieldRow : toItemFieldRow);

        const updated = [...sourceRows];
        updated.splice(target.index, 1, ...children);

        if (isMisc) {
            setRowsMisc(updated);
        } else {
            setRows(updated);
        }
    }

    function handleSplitSubmit(parts) {
        if (splitTarget) {
            handleSplitSingleRow(splitTarget, parts);
        } else {
            handleMergeAndSplit(parts);
        }
    }

    /** The cents the split modal is previewing -- one row, or the whole bill. */
    function getSplitPreviewCents() {
        if (splitTarget) {
            const descriptionField = splitTarget.table === 'misc' ? 'Description' : 'ActionItem';
            const sourceRows = splitTarget.table === 'misc' ? rowsMisc : rows;
            const plainRow = toPlainRows(sourceRows, descriptionField)[splitTarget.index];

            return sumCents(plainRow ? [plainRow] : []);
        }

        const {items, misc} = getPlainRows();

        return sumCents([...items, ...misc]);
    }

    function handleToggleSplitModal(target = null) {
        setSplitTarget(target);
        setSplitModalKey(prev => prev + 1);
        setIsSplitModalOpen(prev => !prev);
    }

    /** Row action wiring for the scissors on both tables. */
    function getSplitRowAction(table) {
        return [{
            action: (item) => handleToggleSplitModal({table: table, index: item.index}),
            icon: ScissorsIcon,
            visible: () => true,
            title: translate("btn.split"),
            order: 10
        }];
    }

    async function addToAttachments(item) {
        const url = Env.getApiUrl("api/" + Resources.WorkOrdersDocuments, {
            DocumentID: item?.DocumentID,
            token: getJWT().access_token,
            id: item?.WorkOrderID
        });


        const response = await fetch(url);
        const blob = await response.blob();

        const file = new File([blob], item?.OriginalFilename || "file", {type: blob.type});


        setAttachments(prev => [...prev, file]);
    }

    const selects = {
        OfficeID: {
            api: 'api/' + Resources.OfficesQuick,
            query: DEFAULT_METADATA_SELECT_SEARCH_QUERY(),
            searchMap: (item) => ({
                label: item.OfficeName,
                value: item.OfficeID
            })
        },
        OrganizationID: {
            api: 'api/' + Resources.Organizations,
            query: {offset: 0, limit: 50},
            searchMap: (item) => ({
                value: item.OrganizationID,
                label: item.LegalName
            })
        },
        ChargeOrganizationID: {
            api: 'api/' + Resources.OrganizationsQuick,
            query: DEFAULT_METADATA_SELECT_SEARCH_QUERY(),
            searchMap: (item) => ({
                label: item.LegalName,
                value: item.OrganizationID
            })
        },
        ChargeToContactID: {
            api: 'api/' + Resources.ContactsQuick,
            query: DEFAULT_METADATA_SELECT_SEARCH_QUERY(),
            searchMap: (item) => ({
                label: item.FirstName + " " + item.LastName,
                value: item.ContactID
            })
        },
        ChargeToOrganizationID: {
            api: 'api/' + Resources.OrganizationsQuick,
            query: DEFAULT_METADATA_SELECT_SEARCH_QUERY(),
            searchMap: (item) => ({
                label: item.LegalName,
                value: item.OrganizationID
            })
        },
        ChargeToTruckID: {
            api: 'api/' + Resources.TrucksQuick,
            query: DEFAULT_METADATA_SELECT_SEARCH_QUERY(),
            searchMap: (item) => ({
                label: item.Truck,
                value: item.TruckID
            })
        },
        AccountID: {
            api: 'api/' + Resources.AccountsQuick,
            query: {},
            searchMap: (item) => ({
                label: item.AccountName,
                value: item.AccountID
            })
        },
        ContactGroupID: {
            api: 'api/' + Resources.ContactGroupsQuick,
            query: DEFAULT_METADATA_SELECT_SEARCH_QUERY(),
            searchMap: (item) => ({
                label: item.ContactGroupName,
                value: item.ContactGroupID
            })
        }
    }

    const basicFieldsToHtml = fieldsToHtml(Object.values(Object.assign({}, fields)), translate, (name, value) => handleFieldsInputChange(name, value), selects)

    const fetchData = () => {
        dispatch(getDialogResource({
            user: getUser(),
            resource: Resources.WorkOrderParts,
            query: {WorkOrderID: selectedItem?.WorkOrderID}
        }))
    }

    const fetchDocumentsData = () => {
        dispatch(getSecondResource({
            user: getUser(),
            resource: Resources.WorkOrdersDocuments,
            query: {id: selectedItem?.WorkOrderID, limit: 200}
        }))
    }

    useEffect(() => {
        fetchData();
        fetchDocumentsData();
    }, []);

    useEffect(() => {
        if (Array.isArray(rows) && Array.isArray(rowsMisc) && fields) {
            const grandTotal = calculateGrandTotal(rows, rowsMisc);

            setFields(prev =>
                FieldsManager.updateField(prev, "TotalAmount", grandTotal)
            );
        }
    }, [rows, rowsMisc]);

    const sumTotalAmount = (arr = []) =>
        arr.reduce((sum, x) => sum + (Number(x?.TotalAmount) || 0), 0);

    /** Item rows exactly as they arrive from the server. Shared by the initial
     *  load and by Reset, so Reset always returns the untouched split. */
    function buildRowsFromData() {
        return dataItems.map(originalItem => {
            const it = structuredClone(originalItem);

            it.TotalAmount = sumTotalAmount(it.Labor) + sumTotalAmount(it.Parts);

            return getFieldsItems(structuredClone(it));
        });
    }

    function buildMiscRowsFromData() {
        return dataMisc.map(it => getMiscFields(structuredClone(it)));
    }

    useEffect(() => {
        if (!Array.isArray(dataItems)) return;

        const newRows = buildRowsFromData();

        setRows(prev =>
            prev.length === newRows.length &&
            prev.every((r, i) => r === newRows[i])
                ? prev
                : newRows
        );

        if (Array.isArray(dataMisc)) {
            const newRowsMisc = buildMiscRowsFromData();

            setRowsMisc(prev =>
                prev.length === newRowsMisc.length &&
                prev.every((r, i) => r === newRowsMisc[i])
                    ? prev
                    : newRowsMisc
            );
        }

    }, [dataItems, dataMisc]);

    function getFormattedFields() {
        return Object.values(rows).map((it, index) => {
            return Object.assign({}, FieldsManager.getFieldKeyValues(it),
                {
                    index: index,
                    Account: it.AccountID?.value?.label,
                    ChargeToContact: it?.ChargeToContactID?.value?.label,
                    ChargeToOrganization: it?.ChargeToOrganizationID?.value?.label,
                    ChargeToTruck: it?.ChargeToTruckID?.value?.label
                })
        })
    }

    function getFormattedFieldsMisc() {
        return Object.values(rowsMisc).map((it, index) => {
            return Object.assign({}, FieldsManager.getFieldKeyValues(it),
                {
                    index: index,
                    Account: it.AccountID?.value?.label,
                    ChargeToContact: it?.ChargeToContactID?.value?.label,
                    ChargeToOrganization: it?.ChargeToOrganizationID?.value?.label,
                    ChargeToTruck: it?.ChargeToTruckID?.value?.label
                })
        })
    }

    function downloadPreview(uri, name) {
        const link = document.createElement("a");
        link.download = name;
        link.href = uri;
        document.body.appendChild(link);
        link.click();
        document.body.removeChild(link);
    }

    function onSubmit() {
        const paramsItems = rows.map(row => {
            const data = {
                ...row,
                Amount: row.TotalAmount
            };

            return FieldsManager.validateFields(data);
        });

        const paramsMisc = rowsMisc.map(row => {
            const data = {
                ...row,
                Amount: row.TotalAmount
            };

            return FieldsManager.validateFields(data);
        });

        const itemsChecked = paramsItems.map(item => ({
            ...item,
            hasError: !FieldsManager.checkFieldsForErrors(item)
        }));

        const miscChecked = paramsMisc.map(item => ({
            ...item,
            hasError: !FieldsManager.checkFieldsForErrors(item)
        }));

        const hasAnyError =
            itemsChecked.some(item => item.hasError) ||
            miscChecked.some(item => item.hasError);

        const noItemErrors = !hasAnyError;
        const validatedFields = FieldsManager.validateFields(fields);
        const fieldErrors = FieldsManager.checkFieldsForErrors(validatedFields);

        let params = {};
        if (noItemErrors && fieldErrors) {
            const paramsFields = FieldsManager.getFieldKeyValues(fields);
            paramsFields.Amount = paramsFields.TotalAmount;
            paramsFields.RefNumber = paramsFields.CustomerReferenceNumber;
            paramsFields.OrganizationID = paramsFields.ChargeOrganizationID;
            delete paramsFields.TotalAmount;
            delete paramsFields.CustomerReferenceNumber;
            delete paramsFields.ChargeOrganizationID;

            const itemsForApi = paramsItems.map(item => {
                const {ActionItem, ...rest} = FieldsManager.getFieldKeyValues(item);

                return {
                    ...rest,
                    Description: ActionItem
                };
            });

            const miscForApi = paramsMisc.map(item => FieldsManager.getFieldKeyValues(item));

            const combinedItems = [...itemsForApi, ...miscForApi]

            params = {
                ...paramsFields,
                WorkOrderID: selectedItem?.WorkOrderID,
                InternalNotes: internalNotes,
                Items: combinedItems
            };

            dispatch(createResource({
                user: LocalStorage.get('user'),
                params: params,
                resource: Resources.WorkOrdersPay,
                query: query,
                file: attachments,
                fileResource: Resources.AccountingExpenseDocumentsAdditional,
                errorMessage: true,
                successMessage: 'The work order has been processed.',
                piggyResource: Resources.WorkOrdersPay
            }))
            onClose();
        } else {
            setFields(validatedFields);
            setRows(paramsItems);
            setRowsMisc(paramsMisc);
            scrollErrorIntoView(rows);
            scrollErrorIntoView(rowsMisc);
            scrollErrorIntoView(fields);
        }
    }

    function onProcessAsPaid() {
        const paramsItems = rows.map(row => {
            const data = {
                ...row,
                Amount: row.TotalAmount
            };

            return FieldsManager.validateFields(data);
        });

        const paramsMisc = rowsMisc.map(row => {
            const data = {
                ...row,
                Amount: row.TotalAmount
            };

            return FieldsManager.validateFields(data);
        });

        const itemsChecked = paramsItems.map(item => ({
            ...item,
            hasError: !FieldsManager.checkFieldsForErrors(item)
        }));

        const miscChecked = paramsMisc.map(item => ({
            ...item,
            hasError: !FieldsManager.checkFieldsForErrors(item)
        }));

        const hasAnyError =
            itemsChecked.some(item => item.hasError) ||
            miscChecked.some(item => item.hasError);

        const noItemErrors = !hasAnyError;
        const validatedFields = FieldsManager.validateFields(fields);
        const fieldErrors = FieldsManager.checkFieldsForErrors(validatedFields);

        let params = {};
        if (noItemErrors && fieldErrors) {
            const paramsFields = FieldsManager.getFieldKeyValues(fields);
            paramsFields.Amount = 0;
            paramsFields.RefNumber = paramsFields.CustomerReferenceNumber;
            paramsFields.OrganizationID = paramsFields.ChargeOrganizationID;
            delete paramsFields.TotalAmount;
            delete paramsFields.CustomerReferenceNumber;
            delete paramsFields.ChargeOrganizationID;

            const itemsForApi = paramsItems.map(item => {
                const {ActionItem, ...rest} = FieldsManager.getFieldKeyValues(item);

                return {
                    ...rest,
                    Description: ActionItem,
                    Amount: 0,
                    TotalAmount: 0
                };
            });

            const miscForApi = paramsMisc.map(item => ({
                ...FieldsManager.getFieldKeyValues(item),
                Amount: 0,
                TotalAmount: 0
            }));
            const combinedItems = [...itemsForApi, ...miscForApi]

            params = {
                ...paramsFields,
                WorkOrderID: selectedItem?.WorkOrderID,
                InternalNotes: internalNotes,
                Items: combinedItems
            };

            dispatch(createResource({
                user: LocalStorage.get('user'),
                params: params,
                resource: Resources.WorkOrdersPay,
                query: query,
                file: attachments,
                fileResource: Resources.AccountingExpenseDocumentsAdditional,
                errorMessage: true,
                successMessage: 'The work order has been processed.',
                piggyResource: Resources.WorkOrdersPay
            }))
            onClose();
        } else {
            setFields(validatedFields);
            setRows(paramsItems);
            setRowsMisc(paramsMisc);
            scrollErrorIntoView(rows);
            scrollErrorIntoView(rowsMisc);
            scrollErrorIntoView(fields);
        }
    }

    return (
        <ModalDefault
            show={show}
            widthClass={'max-w-screen'}
            title={translate("text.process_bill")}

            customButtonsHTML={(
                <div className={'flex items-center'}>
                    {(
                        <Tippy
                            content={translate("text.process_as_paid")}
                        >
                            <button
                                className={"btn btn-primary"}
                                onClick={onProcessAsPaid}
                            >
                                {translate("btn.process_as_paid")}
                            </button>
                        </Tippy>
                    )}
                </div>
            )}
            // isLoading={isDialogLoading}
            closeButtonLabel={translate('Close')}
            onClose={onClose}
            onButtonClick={onSubmit}
            buttonDisabled={!rows.length && !rowsMisc.length}
            buttonLabel={translate("btn.process")}
            customHtmlLeft={
                <>
                <ModalSaveResource
                    key={'wo-pay-charge-to-' + chargeToModalKey}
                    title={translate("text.who_should_be_charged")}
                    widthClass="max-w-md"
                    gridColsClass="grid-cols-1"
                    canSubmit={true}
                    show={!!chargeToTarget}
                    onClose={() => setChargeToTarget(null)}
                    fields={getChargeToFields(chargeToTarget?.item)}
                    handleInputChange={handleChargeToInputChange}
                    metadata={selects}
                    onSubmit={(params, dialogFields) => {
                        if (params) {
                            handleChargeToSubmit(params, dialogFields);
                        }
                    }}
                    translate={translate}
                />

                <ModalSaveResource
                    key={'wo-pay-split-' + splitModalKey}
                    title={splitTarget ? translate("btn.split") : translate("btn.merge_and_split")}
                    widthClass="max-w-sm"
                    gridColsClass="grid-cols-1"
                    canSubmit={true}
                    show={isSplitModalOpen}
                    onClose={() => handleToggleSplitModal()}
                    fields={getSplitFields()}
                    onSubmit={(params) => {
                        if (params) {
                            handleSplitSubmit(Math.max(1, parseInt(params.SplitCount) || 1));
                            handleToggleSplitModal();
                        }
                    }}
                    htmlAfter={(splitFields) => {
                        const parts = Math.max(1, parseInt(splitFields.SplitCount?.value) || 1);
                        const totalCents = getSplitPreviewCents();
                        const amounts = computeSplitAmounts(centsToMoney(totalCents), parts);
                        const first = amounts[0];
                        const last = amounts[amounts.length - 1];

                        return (
                            <div className="px-3 pb-3">
                                <p className="text-tm-gray-900">
                                    {translate("text.each_part")}: {genericMoneyFormatter(first)}
                                    {first !== last && (<> / {genericMoneyFormatter(last)}</>)}
                                </p>

                                <p className="text-sm text-tm-gray-600 mt-1">
                                    {translate("text.total")}: {genericMoneyFormatter(centsToMoney(totalCents))}
                                </p>
                            </div>
                        )
                    }}
                    translate={translate}
                />

                <ModalDefault
                    show={isDocumentPreviewModalOpen}
                    widthClass={'max-w-7xl'}
                    title={translate("modal_heading.Preview")}
                    limitHeight={true}
                    closeButtonLabel={translate('btn.close')}
                    onClose={() => {
                        setIsDocumentPreviewModalOpen(false);
                        setAttachmentFile(null);
                    }}
                    buttonLabel={translate('Download')}

                    onButtonClick={() => {
                        downloadPreview(URL.createObjectURL(attachmentFile), attachmentFile?.name)
                    }}
                >
                    {!!attachmentFile && (
                        <FileViewer
                            filePath={URL.createObjectURL(attachmentFile)}
                            fileType={attachmentFile?.name.split('.').pop()}
                            onError={(e) => {
                                console.log(e)
                            }}
                        />
                    )}
                </ModalDefault>
                </>
            }
        >
            {!!isDialogLoading && (
                <div className={"flex align-middle h-40"}>
                    <Loader/>
                </div>
            )}
            {!isDialogLoading && (
                <div className="grid grid-cols-1 lg:grid-cols-3 divide-x divide-tm-gray-200">
            <div className="grid lg:col-span-2 min-w-0">
                <div className={"grid grid-cols-6 gap-2 m-4"}>
                    {basicFieldsToHtml}
                </div>

                {!!rows.length && (
                    <div className="mt-6 mx-4">
                        <div className="flex items-center justify-between mb-2 ml-4">
                            <div className="text-lg">{translate("text.items")}</div>

                            <div className="flex items-center gap-1.5">
                                <Tooltip content={translate("text.merge_all_in_one_info")}>
                                    <span>
                                        <button
                                            className={LINE_ACTION_BTN_CLASS}
                                            disabled={isDialogLoading}
                                            onClick={handleMergeAll}
                                        >
                                            <ArrowsPointingInIcon className="w-3.5 h-3.5"/>
                                            {translate("btn.merge_all_in_one")}
                                        </button>
                                    </span>
                                </Tooltip>

                                <Tooltip content={translate("text.merge_and_split_info")}>
                                    <span>
                                        <button
                                            className={LINE_ACTION_BTN_CLASS}
                                            disabled={isDialogLoading}
                                            onClick={() => handleToggleSplitModal()}
                                        >
                                            <Squares2X2Icon className="w-3.5 h-3.5"/>
                                            {translate("btn.merge_and_split")}
                                        </button>
                                    </span>
                                </Tooltip>

                                <Tooltip content={translate("text.reset_items_info")}>
                                    <span>
                                        <button
                                            className={LINE_ACTION_BTN_CLASS}
                                            disabled={isDialogLoading}
                                            onClick={handleResetItems}
                                        >
                                            <ArrowPathIcon className="w-3.5 h-3.5"/>
                                            {translate("btn.reset")}
                                        </button>
                                    </span>
                                </Tooltip>
                            </div>
                        </div>

                        <ResourceTable
                            key={"Items"}
                            maxHeightClass="max-h-[calc(100vh-30rem)]"
                            data={getFormattedFields()}
                            fields={getFieldsItems()}
                            // options={this.itemsTableOptions}
                            renderCells={renderCells}
                            actions={getSplitRowAction('items')}
                            options={ITEMS_TABLE_OPTIONS}
                            translate={translate}
                        />
                    </div>
                )}

                {!rows.length && !rowsMisc.length && (
                    <InfoParagraph type={'danger'} message={translate("text.cant_create_no_billable_items")}
                                   className={"m-4"}/>
                )}

                {!!rowsMisc.length && (
                    <div className="mt-6 mx-4">
                        <div className="text-lg ml-4 mb-2">{translate("text.misc")}</div>
                        <ResourceTable
                            key={"Misc"}
                            maxHeightClass="max-h-[calc(100vh-30rem)]"
                            data={getFormattedFieldsMisc()}
                            fields={getMiscFields()}
                            // options={this.itemsTableOptions}
                            renderCells={renderMiscCells}
                            actions={getSplitRowAction('misc')}
                            options={ITEMS_TABLE_OPTIONS}
                            translate={translate}
                        />
                    </div>
                )}

                <div className={"my-4 ml-4"}>
                    <div className={"text-xl"}>Attachments:</div>
                    <div>
                        {attachments.map((it, index) => {
                            return (
                                <div className={"ml-2"}>
                                    {index + 1}. {it.name}
                                    <button onClick={() => {
                                        setAttachmentFile(it);
                                        setIsDocumentPreviewModalOpen(true)
                                    }}>
                                        <EyeIcon className={"btn relative w-5 h-5 left-5 text-blue-400"}/>
                                    </button>

                                    <button
                                        onClick={() => setAttachments(prev => prev.filter(att => att.name !== it.name))}>
                                        <XMarkIcon className={"btn relative w-5 h-5 left-7 text-red-500"}/>
                                    </button>
                                </div>
                            )
                        })}
                    </div>
                </div>
            </div>

            {!previewOpen ? (
                <div className="m-4 grid grid-cols-2 gap-x-3 gap-y-1 text-base lg:col-span-1 min-w-0">
                    {selectedItem?.TruckID && (
                        <button
                            className="col-span-1 btn text-base text-primary min-w-0 break-words whitespace-normal text-left"
                                onClick={() => {
                                    if (checkPerm(Resources.TrucksInfo, UPDATE_PERM)) {
                                        openInNewTab(`/trucks/info/${selectedItem.TruckID}`);
                                    }
                                }}
                        >
                            Truck: {selectedItem?.Truck}
                        </button>
                    )}

                    {selectedItem?.TrailerID && (
                        <button
                            className="col-span-1 btn text-base text-primary min-w-0 break-words whitespace-normal text-left"
                                onClick={() => {
                                    if (checkPerm(Resources.Trailer, UPDATE_PERM)) {
                                        openInNewTab(`/trailers/info/${selectedItem.TrailerID}`);
                                    }
                                }}
                        >
                            Trailer: {selectedItem?.Trailer}
                        </button>
                    )}

                    <div className="font-semibold min-w-0 break-words text-right col-span-2">
                        Work order number: {selectedItem?.AutoCounter}
                    </div>

                    <div className="col-span-2 min-w-0">
                        <div className="mb-2 ml-1">Internal notes</div>
                        <textarea
                            className="w-full min-h-[120px] resize-y rounded-lg border border-gray-300 bg-white px-3 py-2 text-sm text-gray-800 placeholder-gray-400 shadow-sm focus:border-blue-500 focus:outline-none focus:ring-2 focus:ring-blue-200 disabled:bg-gray-100"
                            value={internalNotes}
                            disabled={true}
                        />
                    </div>

                        <div className="mt-6 grid col-span-full min-w-0 text-xs">
                            <div className="text-lg">Documents</div>
                            <ResourceTable
                                maxHeightClass="max-h-[calc(100vh-30rem)]"
                                data={dataDocuments}
                                fields={getDocumentFields()}
                                onView={(item) => {
                                    setPreviewFile(item);
                                    setPreviewOpen(true)
                                }}
                                actions={[
                                    {
                                        tooltipText: () => translate('btn.addAttachment'),
                                        visible: (item) => true,
                                        action: (item) => addToAttachments(item),
                                        icon: () => PlusIcon
                                    }
                                ]}
                                options={{
                                    style: {
                                        condensed: false,
                                        floatingActions: false,
                                        frozenActionColumn: true
                                    }
                                }}
                                translate={translate}
                            />
                        </div>
                    </div>
                ) : !!previewFile && previewOpen && (
                    <div className="lg:col-span-1 min-w-0">
                        <div className="w-full max-w-full h-[500px] overflow-hidden border rounded-lg overflow-y-auto">
                            <div className="flex justify-end items-center">
                                <div className={"text-xl mr-auto m-4"}>File preview</div>

                                <button onClick={() => {
                                    setPreviewOpen(false);
                                }}
                                        className={"mr-4"}>
                                    <XCircleIcon className={"w-10 h-10"}/>
                                </button>
                            </div>
                            <FileViewer
                                filePath={Env.getApiUrl("api/" + Resources.WorkOrdersDocuments, Object.assign({}, {
                                    ['DocumentID']: previewFile && previewFile['DocumentID'],
                                    token: getJWT().access_token,
                                    id: previewFile.WorkOrderID
                                }))}
                                fileType={previewFile?.OriginalFilename?.split('.')?.pop()}

                                onError={(e) => {
                                    console.log(e)
                                }}
                            />
                        </div>
                    </div>
                )}
            </div>)}
        </ModalDefault>
    )
}